import { useEffect } from "react";
import { API_DEVICES, API_DEVICE_ORDERS } from "../../constants/apis";
import { COMPONENT, DEVICE_ORDERS, SELECT, TEXT } from "../../constants/constants";
import { currencyFormat, addSelectOption } from "../../utils/helpers";
import useGet from "../common/useGet";
import { TSet } from "../common/useSet";
import { showConfetti } from "../../components/elements/ConfettiView";
import { reloadData } from "../../components/lists/ListHandler";
import { FundSourceCard, CalculatorCard } from "../../components/elements/CardElements";
import { InfoBox } from "../../components/elements/Elements";
import { INFO_YELLOW, INFO_RED } from "../../constants/colors";
import { PhonePortraitOutline } from "@raresail/react-ionicons";
import styled from "styled-components";
import { primary5 } from "../../constants/colors";
import { API_DEVICE_ORDERS_CREATE } from "../../constants/apis";
import { useAuth } from "../../components/context/AuthContext";

export const useDeviceOrderForm = ({ form, state, updateFormValues }: any) => {
  const device = state || {};
  const { id: deviceId, name, brand, model, cash_price, currency = "UGX", images } = device;
  const primaryImage = images?.find((img: any) => img.is_primary) || images?.[0];
  const { user } = useAuth();

  const planMonths = form?.plan_months;

  // Fetch plan calculation from API when plan_months changes
  const { loadData: calculatePlan, data: planData, loading: calculating } = useGet();

  useEffect(() => {
    if (deviceId && planMonths) {
      calculatePlan({
        api: `${API_DEVICES}${deviceId}/calculate-plan/`,
        params: { plan_months: planMonths },
      });
    }
  }, [deviceId, planMonths]);

  const calculation = planData;

  // Deposit is captured from the wallet at application time — block the
  // submit client-side when the balance can't cover it. (The backend
  // enforces the same rule authoritatively.)
  const walletBalance = parseFloat(String(user?.wallet?.balance ?? 0)) || 0;
  const depositAmount = calculation
    ? parseFloat(String(calculation.deposit_amount ?? 0)) || 0
    : 0;
  const insufficientDeposit = !!calculation && walletBalance < depositAmount;

  const fields = [
    // Device preview
    {
      type: COMPONENT,
      component: (
        <DevicePreview>
          <div className="flexNullCenter gap15">
            <ImageBox className="r10 flexCenter hidden">
              {primaryImage ? (
                <img src={primaryImage.image} alt={name} style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8 }} />
              ) : (
                <PhonePortraitOutline fontSize={24} className="grey" />
              )}
            </ImageBox>
            <div>
              <div className="semiBold textDark font14">{name}</div>
              <div className="font12 grey">{brand} {model && `· ${model}`}</div>
              <div className="font14 bold textDark mt5">{currencyFormat(cash_price, currency)}</div>
            </div>
          </div>
        </DevicePreview>
      ),
    },

    // Plan selection
    {
      field: "plan_months",
      label: "Financing Plan",
      type: SELECT,
      required: true,
      options: [
        { id: 3, name: "3 Months" },
        { id: 6, name: "6 Months" },
        { id: 12, name: "12 Months" },
      ],
      props: {
        helperText: "Choose a repayment duration",
      },
    },

    // Calculation results
    {
      type: COMPONENT,
      visible: !!calculation && !!planMonths,
      component: calculation ? (
        <CalculatorCard
          title="Financing Breakdown"
          fields={{
            device_price: currencyFormat(calculation.cash_price || cash_price),
            deposit_amount: currencyFormat(calculation.deposit_amount),
            financed_amount: currencyFormat(calculation.financed_amount),
            interest_rate: `${calculation.interest_rate}%`,
            monthly_installment: currencyFormat(calculation.monthly_installment),
            total_repayment: currencyFormat(calculation.total_repayment),
          }}
        />
      ) : null,
    },

    // Fund source
    {
      type: COMPONENT,
      visible: !!calculation && !!planMonths,
      component: <FundSourceCard />,
    },

    // Insufficient deposit warning
    {
      type: COMPONENT,
      visible: insufficientDeposit,
      component: (
        <InfoBox color={INFO_RED}>
          <b>Insufficient wallet balance:</b> you need the initial deposit
          of <b>{currencyFormat(depositAmount)}</b> in your wallet to apply
          (available: <b>{currencyFormat(walletBalance)}</b>). Top up your
          wallet and try again.
        </InfoBox>
      ),
    },

    // Disclaimer
    {
      type: COMPONENT,
      visible: !!planMonths && !insufficientDeposit,
      component: (
        <InfoBox color={INFO_YELLOW}>
          <b>Asset Finance Agreement:</b> The device remains Everpesa's property
          until all installments are fully paid. A deposit of{" "}
          <b>{calculation ? currencyFormat(calculation.deposit_amount) : "…"}</b>{" "}
          will be deducted from your wallet and held securely <b>in escrow</b>{" "}
          until your application is decided. If it's not approved, the full
          deposit is returned to your wallet automatically.
        </InfoBox>
      ),
    },
  ];

  const onSubmit = ({ payload }: any): TSet => {
    return {
      api: API_DEVICE_ORDERS_CREATE,
      params: {
        device_id: deviceId,
        plan_months: parseInt(payload.plan_months),
      },
      successMessage: {
        message:
          "Device order created! Your deposit is held safely in escrow — we'll notify you once your application is reviewed.",
        duration: 7,
      },
      onSuccess: () => {
        reloadData(DEVICE_ORDERS);
        showConfetti();
      },
    };
  };

  const allowSubmit =
    !!planMonths && !!calculation && !calculating && !insufficientDeposit;

  return {
    fields,
    onSubmit,
    title: `Finance: ${name || "Device"}`,
    submitText: "Place Order",
    allowSubmit,
  };
};

const DevicePreview = styled.div`
  padding: 12px;
  border-radius: 10px;
  background: ${primary5};
  border: 1px solid ${primary5};
`;

const ImageBox = styled.div`
  width: 60px;
  height: 60px;
  background: #fff;
  flex-shrink: 0;
`;